/**
 * 첨부파일 목록 모달
 * 교수명별 그룹화, PDF 파일 목록 표시, 다운로드/뷰어 열기 기능 제공
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import Modal from '../ui/Modal';
import { attachmentService, Attachment } from '../../services/attachmentService';

// ============================================================================
// Styled Components
// ============================================================================

const ModalContent = styled.div`
  padding: 0;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  max-height: 60vh;
  overflow-x: hidden;
  overflow-y: auto;
  box-sizing: border-box;
`;

const GroupSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  min-width: 0;
`;

const GroupHeader = styled.button<{ $isExpanded: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: ${props => props.theme.spacing.md};
  background: ${props => props.$isExpanded ? props.theme.colors.book.color4b : 'transparent'};
  border: none;
  border-radius: ${props => props.theme.borderRadius.lg};
  cursor: pointer;
  transition: background 0.2s;
  min-width: 0;
  outline: none;
  font-size: ${props => props.theme.fontSize.base};
  font-weight: ${props => props.theme.fontWeight.normal};
  color: ${props => props.$isExpanded ? props.theme.colors.white : props.theme.colors.book.color1b};
  text-align: left;
`;

const GroupHeaderTitle = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const ChevronIcon = styled.span<{ $isExpanded: boolean }>`
  flex-shrink: 0;
  display: inline-flex;
  transition: transform 0.2s;
  transform: rotate(${props => props.$isExpanded ? '180deg' : '0deg'});
  svg path {
    fill: ${props => props.$isExpanded ? props.theme.colors.white : props.theme.colors.book.color1b};
  }
`;

const GroupContent = styled.div`
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  min-width: 0;
  overflow: hidden;
`;

const AttachmentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const AttachmentItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${props => props.theme.spacing.lg};
  min-width: 0;
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  flex: 1;
  cursor: pointer;
  min-width: 0;
`;

const BulletIcon = styled.span`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #D9D9D9;
  flex-shrink: 0;
`;

const FileDetails = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  min-width: 0;
  ellipsis: true;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FileName = styled.span`
  font-size: 15px;
  color: ${props => props.theme.colors.book.color6b};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FileSize = styled.span`
  font-size: 15px;
  color: ${props => props.theme.colors.book.color6b};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 6px;
  flex-shrink: 0;
`;

const OPENED_FILES_STORAGE_PREFIX = 'attachment-opened-';

function getOpenedFileUuids(bookUid: string): Set<string> {
  try {
    const stored = localStorage.getItem(`${OPENED_FILES_STORAGE_PREFIX}${bookUid}`);
    if (!stored) return new Set();
    const parsed = JSON.parse(stored) as string[];
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
}

function addOpenedFileUuid(bookUid: string, fileUuid: string): void {
  try {
    const set = getOpenedFileUuids(bookUid);
    set.add(fileUuid);
    localStorage.setItem(`${OPENED_FILES_STORAGE_PREFIX}${bookUid}`, JSON.stringify([...set]));
  } catch { /* ignore */ }
}

const OpenIconButton = styled.button<{ $isOpened: boolean }>`
  border: none;
  outline: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  tranform: translateY(-6px);
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: var(--text-secondary, #666);
  font-size: 14px;
`;

const LoadingState = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: var(--text-secondary, #666);
  font-size: 14px;
`;

// ============================================================================
// Props Interface
// ============================================================================

export interface AttachmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  bookUid: string;
  onOpenPdf: (attachment: Attachment) => void;
}

// ============================================================================
// Component
// ============================================================================

type GroupedAttachments = Record<string, Attachment[]>;

const AttachmentModal: React.FC<AttachmentModalProps> = ({
  isOpen,
  onClose,
  bookUid,
  onOpenPdf,
}) => {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [openedFileUuids, setOpenedFileUuids] = useState<Set<string>>(() => getOpenedFileUuids(bookUid));

  const groupedByProfessor = useMemo((): GroupedAttachments => {
    const map: GroupedAttachments = {};
    for (const att of attachments) {
      const key = att.professor_name || '(미지정)';
      if (!map[key]) map[key] = [];
      map[key].push(att);
    }
    return map;
  }, [attachments]);

  const toggleGroup = useCallback((professorName: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(professorName)) {
        next.delete(professorName);
      } else {
        next.add(professorName);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    const keys = Object.keys(groupedByProfessor);
    if (keys.length > 0 && expandedGroups.size === 0) {
      setExpandedGroups(new Set([keys[0]]));
    }
  }, [groupedByProfessor]);

  const loadAttachments = useCallback(async () => {
    if (!bookUid) return;
    setLoading(true);
    try {
      const data = await attachmentService.getAttachments(bookUid);
      const pdfFiles = data.filter(
        (file) => file.file_type === 'application/pdf' || file.file_name.toLowerCase().endsWith('.pdf')
      );
      setAttachments(pdfFiles);
    } catch (error) {
      // console.error('[AttachmentModal] 첨부파일 로드 실패:', error);
    } finally {
      setLoading(false);
    }
  }, [bookUid]);

  useEffect(() => {
    if (isOpen) {
      loadAttachments();
      setOpenedFileUuids(getOpenedFileUuids(bookUid));
    }
  }, [isOpen, loadAttachments, bookUid]);

  const handleOpenPdf = (e: React.MouseEvent, attachment: Attachment) => {
    e.preventDefault();
    e.stopPropagation();
    addOpenedFileUuid(bookUid, attachment.file_uuid);
    setOpenedFileUuids((prev) => new Set([...prev, attachment.file_uuid]));
    onClose();
    onOpenPdf(attachment);
  };

  const handleDownload = (e: React.MouseEvent, attachment: Attachment) => {
    e.preventDefault();
    e.stopPropagation();
    attachmentService.downloadFile(attachment.file_uuid, attachment.file_name);
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title="첨부파일" 
      width={420} 
      height={480} 
      position={{ top: '80px', left: '50%'}} 
      containerStyle={{ transform: 'translateX(-50%)' }} 
      showCloseButton={true} 
      showOverlay={false}
    >
      <ModalContent>
        {!bookUid ? (
          <EmptyState>책을 먼저 선택해주세요.</EmptyState>
        ) : loading ? (
          <LoadingState>첨부파일을 불러오는 중...</LoadingState>
        ) : attachments.length === 0 ? (
          <EmptyState>첨부된 PDF 파일이 없습니다.</EmptyState>
        ) : (
          <>
            {Object.entries(groupedByProfessor).map(([professorName, items]) => {
              const isExpanded = expandedGroups.has(professorName);
              return (
                <GroupSection key={professorName}>
                  <GroupHeader
                    type="button"
                    $isExpanded={isExpanded}
                    onClick={() => toggleGroup(professorName)}
                  >
                    <GroupHeaderTitle>{professorName} ({items.length})</GroupHeaderTitle>
                    <ChevronIcon $isExpanded={isExpanded}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="8" viewBox="0 0 16 8" fill="none">
                      <path fillRule="evenodd" clipRule="evenodd" d="M7.28052 0.180478C7.41643 0.0640146 7.58952 0 7.76852 0C7.94751 0 8.1206 0.0640146 8.25652 0.180478L15.2565 6.18048C15.3314 6.24456 15.3929 6.32276 15.4375 6.41062C15.4821 6.49847 15.509 6.59425 15.5166 6.6925C15.5242 6.79074 15.5124 6.88952 15.4819 6.9832C15.4513 7.07688 15.4026 7.16362 15.3385 7.23848C15.2744 7.31333 15.1962 7.37483 15.1084 7.41946C15.0205 7.46409 14.9247 7.49098 14.8265 7.4986C14.7283 7.50621 14.6295 7.4944 14.5358 7.46384C14.4421 7.43328 14.3554 7.38456 14.2805 7.32048L7.76852 1.73748L1.25652 7.31848C1.18236 7.38638 1.09532 7.43868 1.00055 7.47227C0.905781 7.50586 0.805227 7.52005 0.704864 7.514C0.604502 7.50795 0.50638 7.48178 0.416334 7.43705C0.326287 7.39232 0.246155 7.32994 0.180699 7.25362C0.115243 7.1773 0.0658013 7.08859 0.0353128 6.99278C0.00482424 6.89697 -0.00608834 6.79601 0.00322352 6.6959C0.0125354 6.59578 0.0418816 6.49857 0.0895177 6.41002C0.137154 6.32148 0.202107 6.24342 0.280516 6.18048L7.28052 0.180478Z" fill="#4B4B4B"/>
                    </svg>
                    </ChevronIcon>
                  </GroupHeader>
                  {isExpanded && (
                    <GroupContent>
                      <AttachmentList>
                        {items.map((attachment) => (
                          <AttachmentItem key={attachment.file_uuid}>
                            <FileInfo onClick={(e) => handleOpenPdf(e, attachment)}>
                              <BulletIcon />
                              <FileDetails>
                                <FileName title={attachment.file_name}>{attachment.file_name}</FileName>
                                <FileSize>{attachmentService.formatFileSize(attachment.file_size)}</FileSize>
                              </FileDetails>
                            </FileInfo>
                            <ActionButtons>
                              <OpenIconButton
                                type="button"
                                $isOpened={false}
                                onClick={(e) => handleDownload(e, attachment)}
                                title="파일 다운로드"
                                aria-label="파일 다운로드"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                  <path fillRule="evenodd" clipRule="evenodd" d="M10.0003 1.0415C10.1661 1.0415 10.3251 1.10735 10.4423 1.22456C10.5595 1.34177 10.6253 1.50074 10.6253 1.6665V10.8107L12.0253 9.1765C12.0787 9.11413 12.1439 9.06288 12.2171 9.02568C12.2903 8.98849 12.3701 8.96608 12.452 8.95974C12.5339 8.95339 12.6162 8.96323 12.6943 8.9887C12.7723 9.01417 12.8446 9.05477 12.907 9.10817C12.9694 9.16158 13.0206 9.22674 13.0578 9.29995C13.095 9.37316 13.1174 9.45298 13.1238 9.53485C13.1301 9.61672 13.1203 9.69904 13.0948 9.77711C13.0693 9.85517 13.0287 9.92746 12.9753 9.98984L10.4753 12.9065C10.4167 12.9751 10.3438 13.0302 10.2618 13.068C10.1798 13.1057 10.0906 13.1253 10.0003 13.1253C9.91005 13.1253 9.82084 13.1057 9.73884 13.068C9.65684 13.0302 9.584 12.9751 9.52533 12.9065L7.02533 9.98984C6.97192 9.92746 6.93133 9.85517 6.90586 9.77711C6.88039 9.69904 6.87055 9.61672 6.87689 9.53485C6.88324 9.45298 6.90565 9.37316 6.94284 9.29995C6.98003 9.22674 7.03128 9.16158 7.09366 9.10817C7.15604 9.05477 7.22832 9.01417 7.30639 8.9887C7.38446 8.96323 7.46677 8.95339 7.54864 8.95974C7.63051 8.96608 7.71033 8.98849 7.78354 9.02568C7.85675 9.06288 7.92192 9.11413 7.97533 9.1765L9.37533 10.8098V1.6665C9.37533 1.50074 9.44117 1.34177 9.55838 1.22456C9.67559 1.10735 9.83457 1.0415 10.0003 1.0415ZM5.83033 6.8765C5.99609 6.87562 6.15541 6.94062 6.27324 7.05721C6.39108 7.17379 6.45777 7.33241 6.45866 7.49817C6.45954 7.66393 6.39454 7.82325 6.27796 7.94109C6.16137 8.05892 6.00275 8.12562 5.83699 8.1265C4.92616 8.1315 4.28033 8.15484 3.78949 8.24484C3.31783 8.33234 3.04366 8.4715 2.84116 8.674C2.61033 8.90484 2.46033 9.229 2.37783 9.84067C2.29366 10.4698 2.29199 11.304 2.29199 12.4998V13.3332C2.29199 14.5298 2.29366 15.364 2.37783 15.9932C2.46033 16.6048 2.61116 16.9282 2.84116 17.1598C3.07199 17.3898 3.39533 17.5398 4.00783 17.6223C4.63616 17.7073 5.47116 17.7082 6.66699 17.7082H13.3337C14.5295 17.7082 15.3637 17.7073 15.9937 17.6223C16.6053 17.5398 16.9287 17.3898 17.1595 17.159C17.3903 16.9282 17.5403 16.6048 17.6228 15.9932C17.707 15.364 17.7087 14.5298 17.7087 13.3332V12.4998C17.7087 11.304 17.707 10.4698 17.6228 9.83984C17.5403 9.229 17.3895 8.90484 17.1595 8.674C16.9562 8.4715 16.6828 8.33234 16.2112 8.24484C15.7203 8.15484 15.0745 8.1315 14.1637 8.1265C14.0816 8.12607 14.0004 8.10947 13.9247 8.07765C13.8491 8.04584 13.7804 7.99943 13.7227 7.94109C13.665 7.88274 13.6193 7.8136 13.5883 7.7376C13.5573 7.66161 13.5416 7.58025 13.542 7.49817C13.5424 7.41609 13.559 7.33491 13.5908 7.25925C13.6227 7.18359 13.6691 7.11493 13.7274 7.05721C13.7858 6.99948 13.8549 6.95381 13.9309 6.9228C14.0069 6.8918 14.0882 6.87607 14.1703 6.8765C15.072 6.8815 15.8228 6.90317 16.437 7.01567C17.0687 7.13234 17.6062 7.35317 18.0437 7.79067C18.5453 8.2915 18.7603 8.924 18.862 9.674C18.9587 10.3957 18.9587 11.3148 18.9587 12.454V13.379C18.9587 14.519 18.9587 15.4373 18.862 16.1598C18.7603 16.9098 18.5453 17.5415 18.0437 18.0432C17.542 18.5448 16.9103 18.7598 16.1603 18.8615C15.4378 18.9582 14.5187 18.9582 13.3795 18.9582H6.62116C5.48199 18.9582 4.56283 18.9582 3.84033 18.8615C3.09033 18.7607 2.45866 18.5448 1.95699 18.0432C1.45533 17.5415 1.24033 16.9098 1.13949 16.1598C1.04199 15.4373 1.04199 14.5182 1.04199 13.379V12.454C1.04199 11.3148 1.04199 10.3957 1.13949 9.67317C1.23949 8.92317 1.45616 8.2915 1.95699 7.78984C2.39449 7.35317 2.93199 7.1315 3.56366 7.01567C4.17783 6.90317 4.92866 6.8815 5.83033 6.8765Z" fill="#9B9B9B"/>
                                </svg>
                              </OpenIconButton>
                            </ActionButtons>
                          </AttachmentItem>
                        ))}
                      </AttachmentList>
                    </GroupContent>
                  )}
                </GroupSection>
              );
            })}
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default AttachmentModal;
